import React from "react";

const DEFAULT_LINES = [
  "Thinking of You",
  "You’re in my heart and in my thoughts.",
  "Here’s an invitation to experience an extraordinary connection.",
];

// expiresOn is the Valid Till calendar day (YYYY-MM-DD), shown without timezone shifts.
const formatExpiry = (day) => {
  if (!day) return "";
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

// The printable gift card the purchaser gives to their recipient (landscape,
// 11:8.5). `lines` are the three chosen lines (opening, personal message,
// invitation). The code, amount ("worth up to", only if the giver chose to
// show it) and expiry come from the gift itself, never from the wording.
// The QR code (rendered by the backend) opens the redeem page with the code
// already filled in. Nothing about the purchaser appears unless they typed a
// "from" name.
//
// `color` is the chosen background (hex). `plain` prints it on white instead
// (saves ink). `side` is "front" (default) or "back" (the VerySoul side, in the
// same color), and `size="half"` is the envelope-sized card (8.5 x 5.5 in,
// a shorter shape), typeset on its own so nothing is squeezed.
export const DEFAULT_CARD_COLOR = "#D8E7D1";

export default function GiftCard({ code, qrSvg, redeemUrl, worthUpTo, expiresOn, lines, fromName, color, plain, size, side }) {
  const shown = lines && lines.length ? lines : DEFAULT_LINES;
  const expiry = formatExpiry(expiresOn);
  const redeemAt = redeemUrl ? redeemUrl.split("?")[0].replace(/^https?:\/\//, "") : "verysoul.com";

  const className = `vs-card ${size === "half" ? "vs-card-half" : ""} ${plain ? "vs-card-plain" : ""} ${side === "back" ? "vs-card-back" : ""}`;
  const style = { "--vs-card-bg": plain ? "#ffffff" : color || DEFAULT_CARD_COLOR };

  if (side === "back") {
    return (
      <div className={className} style={style}>
        <div className="vs-card-inner">
          <svg className="vs-back-ornament" viewBox="0 0 100 100" aria-hidden="true">
            <defs>
              <linearGradient id="vs-gold" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#e9c978" />
                <stop offset="1" stopColor="#a97b22" />
              </linearGradient>
            </defs>
            <g fill="none" stroke="url(#vs-gold)" strokeWidth="4.2" strokeLinecap="round" strokeLinejoin="round">
              {[0, 90, 180, 270].map((angle) => (
                <path
                  key={angle}
                  transform={`rotate(${angle} 50 50)`}
                  d="M50 47 C43 33 31 31 31 22 C31 13 41 10 46 17 M50 47 C57 33 69 31 69 22 C69 13 59 10 54 17"
                />
              ))}
            </g>
          </svg>
          <p className="vs-back-wordmark">VerySoul</p>
          <p className="vs-back-url">VERYSOUL.COM</p>
          <div className="vs-back-rule" aria-hidden="true">
            <i />
            <b>✦</b>
            <i />
          </div>
          <p className="vs-back-line vs-back-line-lg">
            Trusted Mediums.
            <br />
            Genuine Connections.
          </p>
          <span className="vs-back-heart" aria-hidden="true">
            ♥
          </span>
          <p className="vs-back-line">Heartfelt, evidential connections with loved ones who have passed.</p>
          <span className="vs-back-heart" aria-hidden="true">
            ♥
          </span>
          <p className="vs-back-line vs-back-line-lg">Safe. Simple. Supportive.</p>
          <p className="vs-back-foot">
            Private sessions through Zoom.
            <br />
            Learn more at verysoul.com. Email info@verysoul.com for assistance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={className} style={style}>
      <div className="vs-card-inner">
        <p className="vs-card-eyebrow">A Gift of Connection</p>
        <h2 className="vs-card-title">{shown[0]}</h2>
        {shown[1] && <p className="vs-card-sentiment">{shown[1]}</p>}
        <div className="vs-card-divider" aria-hidden="true" />
        <p className="vs-card-session">A private mediumship session on Zoom</p>
        {shown[2] && <p className="vs-card-invite">{shown[2]}</p>}
        {fromName && <p className="vs-card-from">With love, {fromName}</p>}

        <div className="vs-card-details">
          <div className="vs-card-detail vs-card-detail-code">
            <span className="vs-card-label">Redemption code</span>
            <span className="vs-card-value vs-card-code">{code}</span>
          </div>
          {worthUpTo && (
            <div className="vs-card-detail">
              <span className="vs-card-label">Max amount</span>
              <span className="vs-card-value">
                {worthUpTo.currency === "USD" ? "$" : worthUpTo.currency + " "}
                {Number(worthUpTo.amount).toFixed(0)}
              </span>
            </div>
          )}
          {expiry && (
            <div className="vs-card-detail">
              <span className="vs-card-label">Valid Till</span>
              <span className="vs-card-value">{expiry}</span>
            </div>
          )}
          {qrSvg && <img className="vs-card-qr" src={qrSvg} alt="QR code to redeem this gift" />}
        </div>
        <p className="vs-card-redeem">
          To redeem, visit <b>{redeemAt}</b> and enter your code{qrSvg ? ", or scan the QR code" : ""}.
        </p>
      </div>
    </div>
  );
}
